import { Injectable } from '@angular/core';
import { ApiService, HttpMethod } from '../api/api.service';
import { CreateHouseholdRequest, Household, HouseholdQueryParams, GetHouseholdsResponse, UpdateHouseholdRequest, HouseholdDetails } from '../../models/households.model';


@Injectable({
  providedIn: 'root'
})
export class HouseholdsService {
  private readonly baseUrl = 'households'; // Base route for the households API

  constructor(private apiService: ApiService) { }

  /**
   * Create a new household
   * @param householdData The household data to create
   * @returns Promise with confirmation response
   */
  public async createHousehold(householdData: CreateHouseholdRequest): Promise<any> {
    return this.apiService.call<any>(HttpMethod.POST, this.baseUrl, householdData);
  }

  /**
   * List households with optional filters
   * @param params Optional query parameters for filtering
   * @returns Promise with household list response
   */
  public async listHouseholds(params?: HouseholdQueryParams): Promise<GetHouseholdsResponse> {
    // Convert params object to URL search params
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const queryString = queryParams.toString();
    const url = `${this.baseUrl}${queryString ? `?${queryString}` : ''}`;
    
    return this.apiService.call<GetHouseholdsResponse>(HttpMethod.GET, url);
  }

  /**
   * Update an existing household
   * @param householdId The ID of the household to update
   * @param householdData The updated household data
   * @returns Promise with confirmation response
   */
  public async updateHousehold(householdId: number, householdData: UpdateHouseholdRequest): Promise<any> {
    return this.apiService.call<any>(HttpMethod.PUT, `${this.baseUrl}/${householdId}`, householdData);
  }

  /**
   * Delete a household
   * @param householdId The ID of the household to delete
   * @returns Promise with confirmation response
   */
  public async deleteHousehold(householdId: number): Promise<any> {
    return this.apiService.call<any>(HttpMethod.DELETE, `${this.baseUrl}/${householdId}`);
  }

  /**
   * Get a household by its ID
   * @param householdId The ID of the household to retrieve
   * @returns Promise with household details
   */
  public async getHouseholdById(householdId: string): Promise<HouseholdDetails> {
    return this.apiService.call<HouseholdDetails>(HttpMethod.GET, `${this.baseUrl}/${householdId}`);
  }

  /**
   * Get all households for a specific organization.
   *
   * Hits `GET /households/organization/:organizationId`, which takes an
   * explicit `organizationId` path param. Use this when you need to ask the
   * backend about a firm other than the caller's currently active one (e.g.
   * the OSJ overview iterates every firm in the active group).
   *
   * For listing households of the caller's currently active firm, use
   * `listHouseholds()` — that route is scoped on the server side via the
   * active-context store (super admin / supervisor with an active firm
   * selected) and via the home org (manager).
   *
   * Returns the full unpaginated list — callers needing only a count can
   * use `.length`.
   */
  public async getHouseholdsByOrganization(organizationId: number): Promise<Household[]> {
    return this.apiService.call<Household[]>(HttpMethod.GET, `${this.baseUrl}/organization/${organizationId}`);
  }
}
